import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, formatDate, formatIDR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import {
  ArrowRight, ShieldCheck, Plane, MessageSquare, Receipt, CreditCard, Lock,
  Sparkles, Search, MapPin, CheckCircle2, ShoppingBag, Globe, Award,
} from "lucide-react";

const HERO_LOGO = "/icons/icon-512.png";

export default function Landing() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [landing, setLanding] = useState(null);

  useEffect(() => {
    api.get("/public/landing").then((r) => setLanding(r.data)).catch(() => {});
  }, []);

  const stats = landing?.stats || { transactions: 0, protected_volume: 0, completed: 0, buyers: 0, jastipers: 0 };
  const trips = landing?.featured_trips || [];

  return (
    <div className="space-y-24 sm:space-y-32 pb-20" data-testid="landing-page">
      {/* ─── HERO ───────────────────────────────────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center pt-4 sm:pt-12" data-testid="hero-section">
        <div className="lg:col-span-7 space-y-6 sm:space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface-alt)"}}>
            <ShieldCheck size={14} strokeWidth={2} style={{color: "var(--app-brand)"}} />
            <span className="text-xs font-mono uppercase tracking-wider" style={{color: "var(--app-brand)"}}>
              {t("hero.escrow_badge")}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1] sm:leading-[0.95] tracking-tighter">
            {t("hero.heading_jastip")} <span className="italic font-normal" style={{color: "var(--app-brand)"}}>{t("hero.heading_safe")}</span>.<br />
            <span style={{color: "var(--app-brand)"}}>{t("hero.heading_protected")}</span>.
          </h1>
          <p className="text-lg max-w-xl leading-relaxed" style={{color: "var(--app-muted)"}}>
            {t("hero.desc")}
          </p>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Link
              to={user ? "/dashboard" : "/auth"}
              className="btn-primary-flat group text-base px-7 py-3.5"
              data-testid="hero-cta-unified"
            >
              <ShieldCheck size={18} strokeWidth={1.8} />
              {user ? t("hero.cta_dashboard") : t("hero.cta_unified")}
              <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            {!user && (
              <div
                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-wider border"
                style={{ borderColor: "var(--app-border)", color: "var(--app-muted)" }}
                data-testid="hero-unified-account-hint"
              >
                <Sparkles size={12} strokeWidth={2} style={{ color: "var(--app-brand)" }} />
                <span>{t("hero.unified_hint")}</span>
              </div>
            )}
          </div>

          {/* Live stats */}
          <div className="grid grid-cols-3 gap-0 border-t pt-8 mt-8" style={{borderColor: "var(--app-border)"}} data-testid="hero-stats">
            <StatTile label={t("hero.stat_transactions")} value={stats.transactions.toLocaleString(lang === "id" ? "id-ID" : "en-US")} sub={t("hero.stat_running")} />
            <StatTile label={t("hero.stat_volume")} value={formatCompactIDR(stats.protected_volume)} sub={t("hero.stat_volume_sub")} border />
            <StatTile label={t("hero.stat_community")} value={(stats.buyers + stats.jastipers).toLocaleString(lang === "id" ? "id-ID" : "en-US")} sub={`${stats.buyers} ${t("dash.mode_buyer")} + ${stats.jastipers} ${t("dash.mode_jastiper")}`} />
          </div>
        </div>

        {/* Shield logo */}
        <div className="lg:col-span-5 relative flex items-center justify-center" data-testid="hero-shield">
          <div
            className="absolute inset-0 blur-3xl opacity-30"
            style={{background: "radial-gradient(circle at center, var(--app-brand) 0%, transparent 70%)"}}
          />
          <img
            src={HERO_LOGO}
            alt="Jastip-Safe Shield"
            className="relative w-72 h-72 lg:w-96 lg:h-96 object-contain drop-shadow-2xl"
          />
          <div className="absolute bottom-4 right-4 px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider border-2 rotate-3 shadow-lg"
               style={{borderColor: "var(--app-brand)", backgroundColor: "var(--app-surface)", color: "var(--app-brand)"}}>
            🛡 {lang === "id" ? `Dipercaya oleh ${stats.buyers + stats.jastipers} pengguna` : `Trusted by ${stats.buyers + stats.jastipers} users`}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─────────────────────────────────────────── */}
      <section className="space-y-12" data-testid="how-it-works-section">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          <div className="label-caps" style={{color: "var(--app-brand)"}}>{t("landing.how_label")}</div>
          <h2 className="font-display text-3xl sm:text-5xl tracking-tighter">
            {t("landing.how_title")}
          </h2>
          <p style={{color: "var(--app-muted)"}}>
            {t("landing.how_desc")}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-y" style={{borderColor: "var(--app-border)"}}>
          {[
            { num: "01", icon: CreditCard, title: t("landing.step1_title"), desc: t("landing.step1_desc") },
            { num: "02", icon: Lock, title: t("landing.step2_title"), desc: t("landing.step2_desc") },
            { num: "03", icon: ShoppingBag, title: t("landing.step3_title"), desc: t("landing.step3_desc") },
            { num: "04", icon: CheckCircle2, title: t("landing.step4_title"), desc: t("landing.step4_desc") },
          ].map((step, i) => (
            <div
              key={step.num}
              className={`p-6 sm:p-8 ${i < 3 ? "border-r" : ""} ${i < 2 ? "border-b sm:border-b-0" : ""}`}
              style={{borderColor: "var(--app-border)"}}
              data-testid={`step-${step.num}`}
            >
              <div className="font-mono text-xs mb-4" style={{color: "var(--app-muted)"}}>{step.num}</div>
              <step.icon size={28} strokeWidth={1.5} style={{color: "var(--app-brand)"}} className="mb-4" />
              <div className="font-display text-xl mb-2 tracking-tight">{step.title}</div>
              <p className="text-sm leading-relaxed" style={{color: "var(--app-muted)"}}>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── TRUST BADGES ─────────────────────────────────────────── */}
      <section className="space-y-10" data-testid="trust-badges-section">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          <div className="label-caps" style={{color: "var(--app-brand)"}}>{t("landing.trust_label")}</div>
          <h2 className="font-display text-3xl sm:text-5xl tracking-tighter">{t("landing.trust_title")}</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <TrustBadge icon={ShieldCheck} label={t("landing.badge_escrow")} sub={t("landing.badge_escrow_sub")} testid="badge-escrow" />
          <TrustBadge icon={Lock} label={t("landing.badge_encryption")} sub={t("landing.badge_encryption_sub")} testid="badge-encryption" />
          <TrustBadge icon={ShieldCheck} label={t("landing.badge_antibrute")} sub={t("landing.badge_antibrute_sub")} testid="badge-anti-brute-force" />
          <TrustBadge icon={Award} label={t("landing.badge_antifraud")} sub={t("landing.badge_antifraud_sub")} testid="badge-anti-fraud" />
          <TrustBadge icon={CreditCard} label={t("landing.badge_payment")} sub={t("landing.badge_payment_sub")} testid="badge-secure-payment" />
        </div>
      </section>

      {/* ─── FEATURED TRIPS ───────────────────────────────────────── */}
      {trips.length > 0 && (
        <section className="space-y-8" data-testid="featured-trips-section">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <div className="label-caps" style={{color: "var(--app-brand)"}}>{t("landing.featured_label")}</div>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tighter">{t("landing.featured_title")}</h2>
              <p className="mt-2" style={{color: "var(--app-muted)"}}>
                {t("landing.featured_desc")}
              </p>
            </div>
            <Link to="/trips" className="btn-secondary-flat text-sm" data-testid="view-all-trips-link">
              <Search size={14} strokeWidth={1.5} /> {t("landing.view_all_trips")}
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="featured-trips-grid">
            {trips.slice(0, 8).map((tr) => <FeaturedTripCard key={tr.id} trip={tr} lang={lang} t={t} />)}
          </div>
        </section>
      )}

      {/* ─── ANTI-PRICE-WAR ────────────────────── */}
      <section className="border-y py-16" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface-alt)"}} data-testid="privacy-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-5">
            <div className="label-caps" style={{color: "var(--app-brand)"}}>{t("landing.privacy_label")}</div>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tighter">
              {t("landing.privacy_title")}
            </h2>
            <p style={{color: "var(--app-muted)"}} className="leading-relaxed">
              {t("landing.privacy_desc")}
            </p>
            <ul className="space-y-3">
              <PrivacyBullet text={t("landing.privacy_bullet1")} />
              <PrivacyBullet text={t("landing.privacy_bullet2")} />
              <PrivacyBullet text={t("landing.privacy_bullet3")} />
            </ul>
          </div>
          <div className="relative" data-testid="privacy-illustration">
            <div className="border-2 p-8 space-y-4 max-w-sm mx-auto" style={{borderColor: "var(--app-brand)", backgroundColor: "var(--app-surface)"}}>
              <div className="flex items-center gap-2 text-xs label-caps" style={{color: "var(--app-brand)"}}>
                <Lock size={14} strokeWidth={2} /> {t("landing.privacy_chip")}
              </div>
              <div className="aspect-video flex items-center justify-center" style={{backgroundColor: "var(--app-surface-alt)"}}>
                <ShoppingBag size={48} strokeWidth={1} style={{color: "var(--app-muted)"}} />
              </div>
              <div>
                <div className="font-medium">{t("landing.privacy_card_title")}</div>
                <div className="font-mono text-sm mt-1" style={{color: "var(--app-muted)"}}>{t("landing.privacy_card_price")} ••••••</div>
              </div>
              <button className="btn-primary-flat w-full text-sm">
                <MessageSquare size={14} strokeWidth={1.5} /> {t("landing.privacy_card_cta")}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA FOOTER ───────────────────────────────────────────── */}
      <section className="text-center max-w-3xl mx-auto space-y-6" data-testid="cta-footer-section">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 border" style={{borderColor: "var(--app-brand)", color: "var(--app-brand)"}}>
          <Sparkles size={14} strokeWidth={2} />
          <span className="text-xs font-mono uppercase tracking-wider">{t("landing.cta_today")}</span>
        </div>
        <h2 className="font-display text-3xl sm:text-5xl tracking-tighter">
          {t("landing.cta_title")}
        </h2>
        <p style={{color: "var(--app-muted)"}}>
          {t("landing.cta_desc", { n: (stats.buyers + stats.jastipers).toLocaleString(lang === "id" ? "id-ID" : "en-US") })}
        </p>
        <div className="flex flex-wrap justify-center gap-3 pt-2">
          <Link to={user ? "/dashboard" : "/auth"} className="btn-primary-flat text-base px-8 py-3.5" data-testid="cta-footer-primary">
            <ShieldCheck size={18} strokeWidth={1.8} />
            {user ? t("hero.cta_dashboard") : t("hero.cta_unified")}
            <ArrowRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function StatTile({ label, value, sub, border }) {
  return (
    <div className={`${border ? "border-x px-5" : "px-2 sm:px-5"} py-2`} style={{borderColor: "var(--app-border)"}}>
      <div className="text-[10px] uppercase tracking-wider mb-1" style={{color: "var(--app-muted)"}}>{label}</div>
      <div className="font-display text-xl sm:text-2xl tracking-tight">{value}</div>
      <div className="text-[10px] mt-1 font-mono" style={{color: "var(--app-muted)"}}>{sub}</div>
    </div>
  );
}

function TrustBadge({ icon: Icon, label, sub, testid }) {
  return (
    <div className="border p-5 text-center space-y-2 transition-all hover:translate-y-[-2px]" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface)"}} data-testid={testid}>
      <Icon size={28} strokeWidth={1.5} className="mx-auto" style={{color: "var(--app-brand)"}} />
      <div className="text-sm font-medium">{label}</div>
      <div className="text-[10px] uppercase tracking-wider" style={{color: "var(--app-muted)"}}>{sub}</div>
    </div>
  );
}

function PrivacyBullet({ text }) {
  return (
    <li className="flex items-start gap-3">
      <CheckCircle2 size={18} strokeWidth={2} style={{color: "var(--app-brand)"}} className="flex-shrink-0 mt-0.5" />
      <span>{text}</span>
    </li>
  );
}

function FeaturedTripCard({ trip, lang, t }) {
  const firstItem = trip.catalog_items?.[0];
  const itemCount = trip.catalog_items?.length || 0;
  return (
    <Link to={`/trips/${trip.id}`} className="grid-card group block" data-testid={`featured-trip-${trip.id}`}>
      <div className="aspect-[4/3] overflow-hidden relative" style={{backgroundColor: "var(--app-surface-alt)"}}>
        {firstItem?.photo_path ? (
          <img src={fileUrl(firstItem.photo_path)} alt={firstItem.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
            <Plane size={32} strokeWidth={1} style={{color: "var(--app-brand)", opacity: 0.4}} />
          </div>
        )}
        <div className="absolute top-2 left-2 bg-white/90 backdrop-blur px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono">
          {itemCount} {itemCount === 1 ? t("dash.item") : t("dash.items")}
        </div>
        {trip.origin_location && (
          <div className="absolute top-2 right-2 backdrop-blur px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-white" style={{backgroundColor: "var(--app-brand)"}}>
            ✈ {trip.origin_location}
          </div>
        )}
      </div>
      <div className="p-4 space-y-1">
        <div className="font-display text-base tracking-tight flex items-center gap-1.5">
          <MapPin size={12} strokeWidth={1.5} style={{color: "var(--app-brand)"}} />
          {trip.destination_country}
        </div>
        {trip.destination_city && <div className="text-xs" style={{color: "var(--app-muted)"}}>{trip.destination_city}</div>}
        <div className="border-t pt-2 mt-2 flex items-center justify-between text-[11px] font-mono" style={{borderColor: "var(--app-border)", color: "var(--app-muted)"}}>
          <span>{formatDate(trip.departure_date)}</span>
          <span>{trip.jastiper_name}</span>
        </div>
      </div>
    </Link>
  );
}

function formatCompactIDR(n) {
  if (n >= 1_000_000_000) return `Rp ${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `Rp ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `Rp ${(n / 1_000).toFixed(0)}K`;
  return formatIDR(n);
}
